import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Compass, 
  MapPin, 
  Layers, 
  User, 
  BarChart3, 
  Calculator, 
  BookOpen, 
  Settings, 
  LogOut,
  X
} from 'lucide-react';
import styles from './Sidebar.module.css';

const Sidebar = ({ isOpen, onClose, onToggle }) => {
  const { logout } = useAuth0();
  const location = useLocation();

  // Handle ESC key press to close sidebar
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSignOutClick = () => {
    onClose();
    logout();
  };

  const navigationItems = [
    {
      section: 'Main',
      items: [
        { to: '/', label: 'Home', icon: Home },
        { to: '/compass', label: 'Compass', icon: Compass },
        { to: '/explore', label: 'Explore', icon: MapPin },
        { to: '/pathway-planner', label: 'Pathway Planner', icon: Layers },
        { to: '/profile-builder', label: 'Profile Builder', icon: User },
        { to: '/analytics', label: 'Analytics', icon: BarChart3 },
      ]
    },
    {
      section: 'Tools',
      items: [
        { to: '/gpa-calculator', label: 'GPA Calculator', icon: Calculator },
        { to: '/resource-page', label: 'Resources', icon: BookOpen },
      ]
    }
  ];

  return (
    <>
      {/* Overlay backdrop */}
      {isOpen && <div className={styles.overlay} onClick={handleOverlayClick} />}
      
      <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>Quila</h2>
            <button className={styles.closeBtn} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          {navigationItems.map((section) => (
            <div key={section.section} className={styles.section}>
              <h3 className={styles.sectionTitle}>{section.section}</h3>
              <div className={styles.sectionItems}>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.to;
                  
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                      onClick={onClose}
                    >
                      <Icon size={20} className={styles.icon} />
                      <span className={styles.label}>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.accountBtn} onClick={onClose}>
            <Settings size={20} className={styles.icon} />
            <span className={styles.label}>Settings</span>
          </button>
          <button className={styles.signOutBtn} onClick={handleSignOutClick}>
            <LogOut size={20} className={styles.icon} />
            <span className={styles.label}>Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 